/**
 * Admin Routes
 *
 * Routes for admin functionality: integrations, tenants, users, settings.
 */

import { createPermissionBeforeLoad } from '@bb/session-manager';
import { createRoute, redirect } from '@tanstack/react-router';
import React from 'react';
import { AGTableProvider } from '../../contexts';
import * as groups from '../../groups.config';
import { authLayoutRoute } from '../rootRoute';

// Lazy load pages
const Integrations = React.lazy(() => import('../../pages/Integrations/Integrations'));
const IntegrationDetails = React.lazy(
  () => import('../../pages/IntegrationDetails/IntegrationDetails')
);
const NewTenant = React.lazy(() => import('../../pages/NewTenant/NewTenant'));
const Users = React.lazy(() => import('../../pages/Users/Users'));
const Settings = React.lazy(() => import('../../pages/Settings/Settings'));
const Account = React.lazy(() => import('../../pages/Account/Account'));
const TenantDashboard = React.lazy(() => import('../../pages/TenantDashboard/TenantDashboard'));
const LLMStats = React.lazy(() => import('../../pages/LLMStats/LLMStats'));

// Permission checks
const bbAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.BLACKBOILER_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

const companyAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.COMPANY_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

const accountAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.ACCOUNT_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

// ============================================
// INTEGRATIONS
// ============================================
export const integrationsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/integrations',
  beforeLoad: bbAdminPermission,
  component: Integrations,
});

export const integrationDetailsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/integrations/$integrationId',
  beforeLoad: bbAdminPermission,
  component: IntegrationDetails,
});

// ============================================
// TENANTS
// ============================================
export const newTenantRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/tenants/new',
  beforeLoad: bbAdminPermission,
  component: NewTenant,
});

// ============================================
// USERS
// ============================================
/**
 * Users page wrapped with AGTableProvider
 */
const UsersWithProvider = () => (
  <AGTableProvider>
    <Users />
  </AGTableProvider>
);

export const usersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/users',
  beforeLoad: companyAdminPermission,
  component: UsersWithProvider,
});

// ============================================
// SETTINGS
// ============================================
export const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/settings',
  beforeLoad: bbAdminPermission,
  component: Settings,
});

// ============================================
// ACCOUNT
// ============================================
export const accountRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/account',
  beforeLoad: accountAdminPermission,
  component: Account,
});

// ============================================
// TENANT DASHBOARD
// ============================================
export const tenantDashboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/tenant-dashboard',
  beforeLoad: bbAdminPermission,
  component: TenantDashboard,
});

// ============================================
// LLM STATS
// ============================================
export const llmStatsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/llm-stats',
  beforeLoad: bbAdminPermission,
  component: LLMStats,
});

// ============================================
// ASSISTANT — now the home page. Keep this path as a redirect so any
// beta-era /bbweb/assistant bookmarks still land on the (now home) page.
// ============================================
export const assistantRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/assistant',
  beforeLoad: () => {
    throw redirect({ to: '/bbweb/home' });
  },
});

export const adminRoutes = [
  integrationsRoute,
  integrationDetailsRoute,
  newTenantRoute,
  usersRoute,
  settingsRoute,
  accountRoute,
  tenantDashboardRoute,
  llmStatsRoute,
  assistantRoute,
];
